// ==========================================
// pages/Exam/Result.jsx - COMPLETE FIXED VERSION
// ==========================================
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, Button, Box, Divider, Grid, 
  Chip, Alert, CircularProgress, LinearProgress, Card, CardContent
} from '@mui/material';
import { Download, Home, Refresh, CheckCircle, Save } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { 
  calculateTOEFLScore, 
  getCEFRLevel, 
  getCEFRColor, 
  clearExamData, 
  correctCount,
  getCEFRDescription,
  exitFullscreen 
} from '../../utils/helpers';
import axios from '../../services/axios';

const Result = () => {
  const navigate = useNavigate();
  
  // State untuk hasil
  const [scores, setScores] = useState({ listening: 0, structure: 0, reading: 0, total: 0 });
  const [summary, setSummary] = useState({ 
    listening: { correct: 0, total: 50 }, 
    structure: { correct: 0, total: 40 }, 
    reading: { correct: 0, total: 50 } 
  });
  const [cefrLevel, setCefrLevel] = useState('A1');
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [userAnswers, setUserAnswers] = useState({ listening: {}, structure: {}, reading: {} });
  const [correctAnswers, setCorrectAnswers] = useState({ listening: [], structure: [], reading: [] });
  const [sessionInfo, setSessionInfo] = useState({ sessionId: null, completedAt: null });
  
  const maxRetries = 3;
  const hasProcessedRef = useRef(false);

  // ============================================
  // GET CORRECT ANSWERS FROM SERVER
  // ============================================
  const getCorrectAnswers = async () => {
    try {
      console.log('🔍 Fetching correct answers from server...');
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [listeningRes, structureRes, readingRes] = await Promise.all([
        axios.get('/exam/answer-keys/listening', { headers }),
        axios.get('/exam/answer-keys/structure', { headers }),
        axios.get('/exam/answer-keys/reading', { headers })
      ]);

      const correctAnswers = {
        listening: listeningRes.data?.answerKeys || [],
        structure: structureRes.data?.answerKeys || [],
        reading: readingRes.data?.answerKeys || []
      };

      console.log('✅ Correct answers loaded:', {
        listening: correctAnswers.listening.filter(Boolean).length,
        structure: correctAnswers.structure.filter(Boolean).length,
        reading: correctAnswers.reading.filter(Boolean).length
      });

      return correctAnswers;

    } catch (error) {
      console.error('❌ Error fetching correct answers:', error);
      throw new Error('Unable to retrieve answer keys. Please contact the administrator.');
    }
  };

  // ============================================
  // GET USER ANSWERS FROM SERVER (jika ada)
  // ============================================
  const getUserAnswersFromServer = async (sessionId) => {
    try {
      console.log('📝 Fetching user answers from server...');
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`/exam/session/${sessionId}/answers`, { headers });
      
      if (response.data.success && response.data.answers) {
        const answersBySection = {
          listening: {},
          structure: {},
          reading: {}
        };
        
        response.data.answers.forEach(answer => {
          const section = answer.section;
          const questionIndex = answer.question_number - 1;
          
          if (section && questionIndex >= 0) {
            answersBySection[section][questionIndex] = answer.user_answer;
          }
        });
        
        console.log('✅ User answers loaded from server');
        return answersBySection;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error fetching user answers:', error);
      return null;
    }
  };

  // ============================================
  // GET USER ANSWERS FROM LOCALSTORAGE
  // ============================================
  const getUserAnswersFromLocalStorage = () => {
    try {
      const listeningAnswers = JSON.parse(localStorage.getItem('listening_answers') || '{}');
      const structureAnswers = JSON.parse(localStorage.getItem('structure_answers') || '{}');
      const readingAnswers = JSON.parse(localStorage.getItem('reading_answers') || '{}');
      
      return {
        listening: listeningAnswers,
        structure: structureAnswers,
        reading: readingAnswers
      };
    } catch (error) {
      console.error('❌ Error parsing localStorage answers:', error);
      return { listening: {}, structure: {}, reading: {} };
    }
  };

  // ============================================
  // CALCULATE RESULTS
  // ============================================
  const calculateResults = (userAnswers, correctAnswers) => {
    try {
      console.log('=== Starting Result Calculation ===');
      
      if (!correctAnswers) throw new Error('Correct answers not available');

      const lCorrect = correctCount(userAnswers.listening, correctAnswers.listening);
      const sCorrect = correctCount(userAnswers.structure, correctAnswers.structure) || 0;
      const rCorrect = correctCount(userAnswers.reading, correctAnswers.reading);

      console.log('📊 Correct counts:', {
        listening: lCorrect,
        structure: sCorrect,
        reading: rCorrect
      });

      const calculatedScores = calculateTOEFLScore(lCorrect, sCorrect, rCorrect);
      const level = getCEFRLevel(calculatedScores.total);

      console.log('🎯 Calculated scores:', calculatedScores);
      console.log('🏅 CEFR Level:', level);

      const results = {
        scores: calculatedScores,
        summary: {
          listening: { correct: lCorrect, total: 50 },
          structure: { correct: sCorrect, total: 40 },
          reading: { correct: rCorrect, total: 50 },
        },
        cefrLevel: level,
        details: {
          listeningCorrect: lCorrect,
          structureCorrect: sCorrect,
          readingCorrect: rCorrect
        },
        userAnswers: userAnswers,
        correctAnswers: correctAnswers
      };

      setScores(calculatedScores);
      setSummary(results.summary);
      setCefrLevel(level);
      setUserAnswers(userAnswers);
      setCorrectAnswers(correctAnswers);

      return results;

    } catch (err) {
      console.error('❌ Error in calculateResults:', err);
      throw err;
    }
  };

  // ============================================
  // FINALIZE EXAM SESSION
  // ============================================
  const finalizeExamSession = async (sessionId, results) => {
    try {
      console.log('🏁 Finalizing exam session...');
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // 1. Mark session as completed
      const endTime = new Date();
      
      await axios.post(`/exam/session/${sessionId}/complete`, {
        end_time: endTime.toISOString(),
        status: 'completed'
      }, { headers });
      
      // 2. Save exam results
      const response = await axios.post('/exam/submit', {
        session_id: sessionId,
        listening_score: results.scores.listening,
        structure_score: results.scores.structure,
        reading_score: results.scores.reading,
        total_score: results.scores.total,
        cefr_level: results.cefrLevel,
        listening_correct: results.details.listeningCorrect,
        structure_correct: results.details.structureCorrect,
        reading_correct: results.details.readingCorrect,
        time_spent: {
          listening: parseInt(localStorage.getItem('listening_time') || '0'),
          structure: parseInt(localStorage.getItem('structure_time') || '0'),
          reading: parseInt(localStorage.getItem('reading_time') || '0')
        }
      }, { headers });

      if (response.data.success) {
        console.log('✅ Exam session finalized successfully');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to finalize exam');
      }
      
    } catch (error) {
      console.error('❌ Error finalizing exam session:', error);
      throw error;
    }
  };

  // ============================================
  // SAVE RESULTS WITH RETRY
  // ============================================
  const saveResultsWithRetry = async (results, maxRetries = 3) => {
    const sessionId = localStorage.getItem('exam_session_id');
    
    if (!sessionId) {
      throw new Error('Session ID not found. Please restart the exam.');
    }

    setSessionInfo({ 
      sessionId, 
      completedAt: new Date().toISOString() 
    });

    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        setRetryCount(attempt);
        console.log(`📤 Saving attempt ${attempt}/${maxRetries} with session_id: ${sessionId}`);
        
        await finalizeExamSession(parseInt(sessionId), results);
        
        console.log('✅ Successfully saved to server');
        return { success: true };

      } catch (error) {
        lastError = error;
        console.error(`❌ Attempt ${attempt} failed:`, error.message || error);
        
        if (error.response?.status === 401) {
          return { success: false, error: 'Session expired', shouldRedirect: true };
        }
        
        if (attempt < maxRetries) {
          const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    
    console.warn('⚠ All save attempts failed');
    
    return { 
      success: false, 
      error: lastError?.response?.data?.message || lastError?.message || 'Save failed',
    };
  };

  // ============================================
  // MAIN PROCESSING FUNCTION
  // ============================================
  const processResults = async () => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    try {
      setProcessing(true);
      setLoading(true);

      // Cek apakah semua section sudah completed
      const listeningCompleted = localStorage.getItem('listening_completed');
      const structureCompleted = localStorage.getItem('structure_completed');
      const readingCompleted = localStorage.getItem('reading_completed');

      if (listeningCompleted !== 'true' || structureCompleted !== 'true' || readingCompleted !== 'true') {
        alert('You must complete all exam sections first!');
        navigate('/exam/listening', { replace: true });
        return;
      }

      // Keluar dari fullscreen mode
      exitFullscreen();

      // Ambil session ID
      const sessionId = localStorage.getItem('exam_session_id');
      if (!sessionId) {
        throw new Error('Exam session not found. Please restart the exam.');
      }

      // 1. Get correct answers
      const correctAnswers = await getCorrectAnswers();
      
      // 2. Get user answers (prioritas dari server, fallback ke localStorage)
      let userAnswers = await getUserAnswersFromServer(sessionId);
      if (!userAnswers) {
        userAnswers = getUserAnswersFromLocalStorage();
      }

      // 3. Calculate results
      const results = calculateResults(userAnswers, correctAnswers);

      // 4. Save results to server
      setSaving(true);
      const saveResult = await saveResultsWithRetry(results);

      if (saveResult.success) {
        setSaved(true);
        setSaveError(null);

        // Update CEFR level di localStorage
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.cefr_level = results.cefrLevel;
          localStorage.setItem('user', JSON.stringify(user));
        } catch (e) {
          console.error('Error updating user CEFR:', e);
        }

        // Mark as processed
        localStorage.setItem('result_saved', 'true');
        localStorage.setItem('result_saved_at', new Date().toISOString());

        // Save results untuk akses nanti
        localStorage.setItem('last_exam_results', JSON.stringify({
          scores: results.scores,
          summary: results.summary,
          cefrLevel: results.cefrLevel
        }));

      } else {
        if (saveResult.shouldRedirect) {
          setSaveError('Session expired. Redirecting to login...');
          setTimeout(() => {
            localStorage.clear();
            navigate('/login', { replace: true });
          }, 2000);
        } else {
          setSaveError(`Failed to save: ${saveResult.error}. Results are calculated but not saved to server.`);
        }
      }

    } catch (err) {
      console.error('❌ Error in processResults:', err);
      setSaveError(err.message || 'Unknown error occurred');
    } finally {
      setProcessing(false);
      setLoading(false);
      setSaving(false);
    }
  };

  useEffect(() => {
    // Cek jika result sudah pernah di-save sebelumnya
    const resultSaved = localStorage.getItem('result_saved');
    if (resultSaved === 'true') {
      try {
        const saved = localStorage.getItem('last_exam_results');
        if (saved) {
          const parsed = JSON.parse(saved);
          setScores(parsed.scores);
          setSummary(parsed.summary);
          setCefrLevel(parsed.cefrLevel);
          setSaved(true);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Error loading saved results:', e);
      }
    }

    // Process results jika belum diproses
    processResults();
  }, [navigate]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleRetrySave = async () => {
    if (saving) return;
    
    setSaving(true);
    setSaveError(null);
    
    try {
      // Re-calculate dengan data yang ada
      const results = calculateResults(userAnswers, correctAnswers);
      const saveResult = await saveResultsWithRetry(results);
      
      if (saveResult.success) {
        setSaved(true);
      } else {
        setSaveError(`Retry failed: ${saveResult.error}`);
      }
    } catch (error) {
      setSaveError(`Retry failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleContinueToDashboard = () => {
    // Clear exam data
    clearExamData();
    navigate('/dashboard', { replace: true });
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(26, 60, 109);
      doc.text('TOEFL CBT Exam Results', 105, 20, { align: 'center' });
      
      // Exam Info
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${new Date().toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, 20, 35);
      doc.text(`Session ID: ${sessionInfo.sessionId || 'N/A'}`, 20, 42);
      
      // CEFR Level
      doc.setFontSize(16);
      const cefrColor = getCEFRColor(cefrLevel);
      const cefrRGB = hexToRgb(cefrColor);
      doc.setTextColor(cefrRGB.r, cefrRGB.g, cefrRGB.b);
      doc.text(`CEFR Level: ${cefrLevel}`, 20, 60);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(getCEFRDescription(cefrLevel), 20, 67);
      
      // Section Scores Table
      doc.setFontSize(14);
      doc.setTextColor(26, 60, 109);
      doc.text('Section Scores', 20, 85);
      
      const tableData = [
        ['Listening', `${scores.listening}`, `${summary.listening.correct}/${summary.listening.total}`],
        ['Structure', `${scores.structure}`, `${summary.structure.correct}/${summary.structure.total}`],
        ['Reading', `${scores.reading}`, `${summary.reading.correct}/${summary.reading.total}`]
      ];
      
      doc.autoTable({
        startY: 90,
        head: [['Section', 'Score', 'Correct Answers']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [26, 60, 109], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 60, halign: 'center' }
        },
        margin: { left: 20 }
      });
      
      // Total Score
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 120;
      doc.setFontSize(18);
      doc.setTextColor(46, 125, 50);
      doc.text(`Total TOEFL Score: ${scores.total} / 677`, 20, finalY);
      
      // Footer
      const footerY = finalY + 20;
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text('Official TOEFL CBT Exam Results - Generated by TOEFL CBT System', 105, footerY, { align: 'center' });
      
      // Save PDF
      doc.save(`TOEFL_Result_${new Date().getTime()}.pdf`);
      
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const handleRetakeExam = () => {
    if (window.confirm('Are you sure you want to retake the exam? All current exam data will be deleted.')) {
      clearExamData();
      localStorage.removeItem('result_saved');
      localStorage.removeItem('last_exam_results');
      navigate('/exam/listening', { replace: true });
    }
  };

  // ============================================
  // RENDER LOADING STATE
  // ============================================
  if (loading || processing) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Paper elevation={3} sx={{ p: 6, borderRadius: 3 }}>
          <CircularProgress size={60} />
          <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
            {processing ? 'Processing Exam Results...' : 'Loading Results...'}
          </Typography>
          
          {saving && (
            <>
              <LinearProgress sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Saving to server... (Attempt {retryCount}/{maxRetries})
              </Typography>
            </>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please wait while we calculate your scores and save the results.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // ============================================
  // RENDER RESULTS
  // ============================================
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={8} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, position: 'relative' }}>
        
        {/* Success Badge */}
        {saved && (
          <Box sx={{
            position: 'absolute',
            top: -15,
            right: 20,
            bgcolor: '#2e7d32',
            color: 'white',
            px: 3,
            py: 0.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CheckCircle fontSize="small" />
            <Typography variant="caption" fontWeight="bold">
              Saved to Server
            </Typography>
          </Box>
        )}
        
        <Typography variant="h4" fontWeight="bold" textAlign="center" color="#1a3c6d" gutterBottom>
          🎓 TOEFL CBT Exam Results
        </Typography>
        
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
          Session completed on {new Date().toLocaleString()}
        </Typography>

        {/* Status Messages */}
        {saveError && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              !saved && (
                <Button 
                  size="small" 
                  onClick={handleRetrySave} 
                  disabled={saving}
                  startIcon={<Save />}
                >
                  {saving ? 'Saving...' : 'Retry Save'}
                </Button>
              )
            }
          >
            {saveError}
          </Alert>
        )}

        {saved && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ Your exam results have been successfully saved to the server.
          </Alert>
        )}

        {/* CEFR Level Display */}
        <Box sx={{ textAlign: 'center', my: 5 }}>
          <Chip 
            label={`CEFR Level: ${cefrLevel}`}
            sx={{ 
              fontSize: 28, 
              fontWeight: 'bold', 
              py: 3, 
              px: 5,
              bgcolor: getCEFRColor(cefrLevel),
              color: 'white',
              '& .MuiChip-label': {
                px: 2
              }
            }}
          />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
            {getCEFRDescription(cefrLevel)}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Section Scores */}
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Section Breakdown
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={4} sx={{ 
              textAlign: 'center', 
              bgcolor: '#e3f2fd',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="#1565c0" gutterBottom>
                  🎧 Listening
                </Typography>
                <Typography variant="h2" fontWeight="bold" color="#1565c0">
                  {scores.listening}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {summary.listening.correct} / {summary.listening.total} correct answers
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(summary.listening.correct / summary.listening.total) * 100}
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card elevation={4} sx={{ 
              textAlign: 'center', 
              bgcolor: '#f3e5f5',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="#7b1fa2" gutterBottom>
                  📝 Structure
                </Typography>
                <Typography variant="h2" fontWeight="bold" color="#7b1fa2">
                  {scores.structure}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {summary.structure.correct} / {summary.structure.total} correct answers
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(summary.structure.correct / summary.structure.total) * 100}
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card elevation={4} sx={{ 
              textAlign: 'center', 
              bgcolor: '#fff3e0',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="#ef6c00" gutterBottom>
                  📖 Reading
                </Typography>
                <Typography variant="h2" fontWeight="bold" color="#ef6c00">
                  {scores.reading}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {summary.reading.correct} / {summary.reading.total} correct answers
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(summary.reading.correct / summary.reading.total) * 100}
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Total Score */}
        <Card elevation={6} sx={{ 
          textAlign: 'center', 
          bgcolor: '#f1f8e9',
          mb: 5,
          border: '2px solid #2e7d32'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="bold" color="#2e7d32" gutterBottom>
              Total TOEFL Score
            </Typography>
            <Typography variant="h1" fontWeight="bold" color="#2e7d32" sx={{ fontSize: { xs: '3rem', sm: '4rem' } }}>
              {scores.total}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Out of 677 points
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip label={`Listening: ${scores.listening}`} color="primary" variant="outlined" />
              <Chip label={`Structure: ${scores.structure}`} color="secondary" variant="outlined" />
              <Chip label={`Reading: ${scores.reading}`} sx={{ color: '#ef6c00', borderColor: '#ef6c00' }} variant="outlined" />
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownloadPDF}
              disabled={downloading}
              sx={{ 
                bgcolor: '#1a3c6d', 
                py: 1.5,
                '&:hover': { bgcolor: '#0d2b5c' }
              }}
            >
              {downloading ? 'Generating PDF...' : 'Download PDF'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Home />}
              onClick={handleContinueToDashboard}
              sx={{ py: 1.5 }}
            >
              Go to Dashboard
            </Button>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<Refresh />}
              onClick={handleRetakeExam}
              sx={{ py: 1.5 }}
            >
              Retake Exam
            </Button>
          </Grid>
        </Grid>

        {/* Session Info */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px dashed #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary">
            Session ID: {sessionInfo.sessionId || 'Not available'} • 
            Completed: {sessionInfo.completedAt ? new Date(sessionInfo.completedAt).toLocaleString() : 'Not saved'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Result;
